import React, { useState, useEffect } from 'react';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const BookingSummaryPanel = ({ 
  isVisible = false, 
  bookingData = null, 
  onClose = () => {}, 
  onProceed = () => {} 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Sample booking data structure
  const defaultBookingData = {
    accommodation: 'Deluxe Forest Villa',
    checkIn: '2025-09-20',
    checkOut: '2025-09-22',
    guests: 2,
    nights: 2,
    roomRate: 4500,
    activities: [
      { name: 'River Rafting', price: 1200 },
      { name: 'Jungle Safari', price: 800 }
    ],
    taxes: 540,
    total: 11040
  };

  const booking = bookingData || defaultBookingData;

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Desktop Sticky Panel */}
      <div className="hidden lg:block fixed right-6 top-24 w-80 bg-card border border-border rounded-lg shadow-organic-xl z-30">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading font-semibold text-card-foreground">
              Booking Summary
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-md text-muted-foreground hover:text-card-foreground transition-micro"
            >
              <Icon name="X" size={16} />
            </button>
          </div>

          {/* Accommodation Details */}
          <div className="space-y-3 mb-4">
            <div className="flex items-start space-x-3">
              <Icon name="Bed" size={16} className="text-primary mt-1" />
              <div>
                <p className="font-medium text-card-foreground">{booking?.accommodation}</p>
                <p className="text-sm text-muted-foreground">
                  {booking?.guests} guest{booking?.guests > 1 ? 's' : ''} • {booking?.nights} night{booking?.nights > 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Icon name="Calendar" size={16} className="text-primary" />
              <div className="text-sm">
                <span className="text-card-foreground font-mono">
                  {formatDate(booking?.checkIn)} - {formatDate(booking?.checkOut)}
                </span>
              </div>
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="border-t border-border pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Room ({booking?.nights} nights)</span>
              <span className="font-mono text-card-foreground">
                {formatCurrency(booking?.roomRate * booking?.nights)}
              </span>
            </div>

            {booking?.activities?.map((activity, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{activity?.name}</span>
                <span className="font-mono text-card-foreground">
                  {formatCurrency(activity?.price)}
                </span>
              </div>
            ))}

            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Taxes & Fees</span>
              <span className="font-mono text-card-foreground">
                {formatCurrency(booking?.taxes)}
              </span>
            </div>

            <div className="border-t border-border pt-2 flex justify-between font-semibold">
              <span className="text-card-foreground">Total</span>
              <span className="font-mono text-primary text-lg">
                {formatCurrency(booking?.total)}
              </span>
            </div>
          </div>

          {/* Action Button */}
          <div className="mt-6">
            <Button
              variant="default"
              size="default"
              fullWidth
              iconName="CreditCard"
              iconPosition="left"
              onClick={onProceed}
            >
              Proceed to Payment
            </Button>
          </div>
        </div>
      </div>
      {/* Mobile Slide-up Panel */}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-50">
        {/* Collapsed State */}
        {!isExpanded && (
          <div className="bg-card border-t border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-card-foreground">{booking?.accommodation}</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(booking?.checkIn)} - {formatDate(booking?.checkOut)}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="font-mono font-semibold text-primary">
                    {formatCurrency(booking?.total)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <button
                  onClick={() => setIsExpanded(true)}
                  className="p-2 rounded-md bg-primary text-primary-foreground"
                >
                  <Icon name="ChevronUp" size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expanded State */}
        {isExpanded && (
          <div className="bg-card border-t border-border max-h-[80vh] overflow-y-auto">
            <div className="p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading font-semibold text-card-foreground">
                  Booking Summary
                </h3>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-2 rounded-md text-muted-foreground hover:text-card-foreground"
                >
                  <Icon name="ChevronDown" size={16} />
                </button>
              </div>

              {/* Content - Same as desktop */}
              <div className="space-y-3 mb-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Bed" size={16} className="text-primary mt-1" />
                  <div>
                    <p className="font-medium text-card-foreground">{booking?.accommodation}</p>
                    <p className="text-sm text-muted-foreground">
                      {booking?.guests} guest{booking?.guests > 1 ? 's' : ''} • {booking?.nights} night{booking?.nights > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Icon name="Calendar" size={16} className="text-primary" />
                  <div className="text-sm">
                    <span className="text-card-foreground font-mono">
                      {formatDate(booking?.checkIn)} - {formatDate(booking?.checkOut)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-border pt-4 space-y-2 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Room ({booking?.nights} nights)</span>
                  <span className="font-mono text-card-foreground">
                    {formatCurrency(booking?.roomRate * booking?.nights)}
                  </span>
                </div>

                {booking?.activities?.map((activity, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{activity?.name}</span>
                    <span className="font-mono text-card-foreground">
                      {formatCurrency(activity?.price)}
                    </span>
                  </div>
                ))}

                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Taxes & Fees</span>
                  <span className="font-mono text-card-foreground">
                    {formatCurrency(booking?.taxes)}
                  </span>
                </div>

                <div className="border-t border-border pt-2 flex justify-between font-semibold">
                  <span className="text-card-foreground">Total</span>
                  <span className="font-mono text-primary text-lg">
                    {formatCurrency(booking?.total)}
                  </span>
                </div>
              </div>

              {/* Action Button */}
              <Button
                variant="default"
                size="default"
                fullWidth
                iconName="CreditCard"
                iconPosition="left"
                onClick={onProceed}
              >
                Proceed to Payment
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BookingSummaryPanel;